import { RiskService } from "./riskService";
import { ControlService } from "./controlService";
import { BusinessUnitService } from "./businessUnitService";
import { getApiUrl } from "../config/api";

export class UnifiedRiskService {
  static async getRisks(
    contextType,
    contextId,
    organizationId,
    getAccessTokenSilently
  ) {
    if (contextType === "organization") {
      return await RiskService.getOrganizationRisks(
        contextId,
        getAccessTokenSilently
      );
    } else {
      const risks = await RiskService.getRisksForBusinessUnit(
        contextId,
        getAccessTokenSilently
      );
      // Add business unit ID to each risk for consistency
      return risks.map((risk) => ({
        ...risk,
        business_unit_id: contextId,
      }));
    }
  }

  static async createRisk(
    contextType,
    contextId,
    organizationId,
    data,
    getAccessTokenSilently
  ) {
    const riskData = {
      ...data,
      organization_id: organizationId,
      business_unit_id: contextType === "businessUnit" ? contextId : null,
    };
    return await RiskService.createRisk(riskData, getAccessTokenSilently);
  }

  static async updateRisk(riskId, data, getAccessTokenSilently) {
    return await RiskService.updateRisk(riskId, data, getAccessTokenSilently);
  }

  static async deleteRisk(riskId, getAccessTokenSilently) {
    return await RiskService.deleteRisk(riskId, getAccessTokenSilently);
  }
}

export class UnifiedControlService {
  static async getControls(
    contextType,
    contextId,
    organizationId,
    getAccessTokenSilently
  ) {
    const businessUnitId = contextType === "businessUnit" ? contextId : null;
    return await ControlService.getControls(
      organizationId,
      businessUnitId,
      getAccessTokenSilently
    );
  }

  static async createControl(
    contextType,
    contextId,
    organizationId,
    data,
    getAccessTokenSilently
  ) {
    const controlData = {
      ...data,
      organization_id: organizationId,
      business_unit_id: contextType === "businessUnit" ? contextId : null,
    };
    return await ControlService.createControl(
      controlData,
      getAccessTokenSilently
    );
  }

  static async updateControl(controlId, data, getAccessTokenSilently) {
    return await ControlService.updateControl(
      controlId,
      data,
      getAccessTokenSilently
    );
  }

  static async deleteControl(controlId, getAccessTokenSilently) {
    return await ControlService.deleteControl(
      controlId,
      getAccessTokenSilently
    );
  }
}

export class UnifiedUserService {
  static async getUsers(
    contextType,
    contextId,
    organizationId,
    getAccessTokenSilently
  ) {
    if (contextType === "organization") {
      return await this.getOrganizationUsers(contextId, getAccessTokenSilently);
    } else {
      return await this.getBusinessUnitUsers(contextId, getAccessTokenSilently);
    }
  }

  static async getOrganizationUsers(organizationId, getAccessTokenSilently) {
    // Get all business units for this organization
    const businessUnits =
      await BusinessUnitService.getBusinessUnitsForOrganization(
        organizationId,
        getAccessTokenSilently
      );

    // Collect all users from business units
    const allUsersMap = new Map();

    for (const businessUnit of businessUnits) {
      try {
        // Fetch business unit heads
        const heads = await BusinessUnitService.getBusinessUnitHeads(
          businessUnit.id,
          getAccessTokenSilently
        );

        // Fetch business users
        const users = await BusinessUnitService.getBusinessUsers(
          businessUnit.id,
          getAccessTokenSilently
        );

        // Add heads to map
        heads.forEach((head) => {
          const userId = head.id;
          if (!allUsersMap.has(userId)) {
            allUsersMap.set(userId, {
              ...head,
              roles: [],
              businessUnits: [],
            });
          }
          allUsersMap.get(userId).roles.push({
            type: "business_unit_head",
            businessUnit: businessUnit,
          });
          allUsersMap.get(userId).businessUnits.push({
            ...businessUnit,
            role: "head",
          });
        });

        // Add users to map
        users.forEach((user) => {
          const userId = user.id;
          if (!allUsersMap.has(userId)) {
            allUsersMap.set(userId, {
              ...user,
              roles: [],
              businessUnits: [],
            });
          }
          allUsersMap.get(userId).roles.push({
            type: "business_user",
            businessUnit: businessUnit,
          });
          allUsersMap.get(userId).businessUnits.push({
            ...businessUnit,
            role: "user",
          });
        });
      } catch (err) {
        console.error(
          `Error fetching users for business unit ${businessUnit.name}:`,
          err
        );
      }
    }

    // Get current user profile to check if they are an organization admin
    try {
      const currentUserResponse = await fetch(getApiUrl("users/me"), {
        headers: {
          Authorization: `Bearer ${await getAccessTokenSilently()}`,
        },
      });

      if (currentUserResponse.ok) {
        const currentUser = await currentUserResponse.json();

        // Check if current user is an admin of this organization
        const isOrgAdmin = currentUser.permissions?.organizations?.some(
          (org) => org.id === organizationId && org.role === "admin"
        );

        if (isOrgAdmin) {
          // Add current user as organization admin if not already in the map
          const currentUserId = currentUser.id;
          if (!allUsersMap.has(currentUserId)) {
            allUsersMap.set(currentUserId, {
              ...currentUser,
              roles: [
                {
                  type: "org_admin",
                  organization: { id: organizationId },
                },
              ],
              businessUnits: [],
            });
          } else {
            // If user is already in map (from business unit roles), add org admin role
            allUsersMap.get(currentUserId).roles.push({
              type: "org_admin",
              organization: { id: organizationId },
            });
          }
        }
      }
    } catch (err) {
      console.error(
        "Error fetching current user for organization admin check:",
        err
      );
    }

    return Array.from(allUsersMap.values());
  }

  static async getBusinessUnitUsers(businessUnitId, getAccessTokenSilently) {
    // Fetch business unit heads
    const headsResponse = await BusinessUnitService.getBusinessUnitHeads(
      businessUnitId,
      getAccessTokenSilently
    );

    // Fetch business users
    const usersResponse = await BusinessUnitService.getBusinessUsers(
      businessUnitId,
      getAccessTokenSilently
    );

    // Combine all users with role information
    const allUsersData = [
      ...headsResponse.map((user) => ({
        ...user,
        role: "business_unit_head",
        roleDisplay: "Business Unit Head",
        roleBadgeVariant: "default",
        assignedAt: user.assignedAt,
      })),
      ...usersResponse.map((user) => ({
        ...user,
        role: "business_user",
        roleDisplay: "Business User",
        roleBadgeVariant: "secondary",
        assignedAt: user.assignedAt,
      })),
    ];

    // Sort by assigned date (most recent first)
    allUsersData.sort(
      (a, b) => new Date(b.assignedAt) - new Date(a.assignedAt)
    );

    return allUsersData;
  }

  static async removeUser(
    contextType,
    contextId,
    userId,
    roleType,
    getAccessTokenSilently
  ) {
    if (contextType === "organization") {
      // For organization context, we need the business unit ID and role type
      if (roleType === "business_unit_head") {
        await BusinessUnitService.removeBusinessUnitHead(
          contextId, // This would be the business unit ID in org context
          userId,
          getAccessTokenSilently
        );
      } else if (roleType === "business_user") {
        await BusinessUnitService.removeBusinessUser(
          contextId, // This would be the business unit ID in org context
          userId,
          getAccessTokenSilently
        );
      }
    } else {
      // For business unit context
      if (roleType === "business_unit_head") {
        await BusinessUnitService.removeBusinessUnitHead(
          contextId,
          userId,
          getAccessTokenSilently
        );
      } else if (roleType === "business_user") {
        await BusinessUnitService.removeBusinessUser(
          contextId,
          userId,
          getAccessTokenSilently
        );
      }
    }
  }
}
